import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Drawer, Button, Space, Table, Tabs, Tag, Tooltip, Popconfirm } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { CHANNEL_META } from '../Reminders/channelMeta';
import tinycolor from 'tinycolor2';
import ReminderService, { ACTIVITY_TYPES, ActivityType, ReminderFilter, ReminderStateType } from '../../services/ReminderService';
import ProposalService, { OtherProposalItem, ProposalPdfArchiveHistoryItem } from '../../services/ProposalService';
import { openNotification, hasRole } from '../../utils';
import { STATE_LABELS } from '../../views/Reminders/stateLabels';
import '../Companies/Company.scss';
import CompletionDrawer from './CompletionDrawer';
import AllRemindersTab from './AllRemindersTab';
import NoteReminders from './NoteReminders';
import StatusChangeReminders from './StatusChangeReminders';
import ResponsibleChangeReminders from './ResponsibleChangeReminders';
import { http } from '../../http-common';
import AccountCell from '../Reminders/AccountCell';
import { getAssigneAccountCellProps } from '../Reminders/accountCellHelper';
import PenIcon from '../../assets/icons/pen.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import {
  getScheduledEmailStatusColor,
  getScheduledEmailStatusLabel,
  getScheduledEmailTemplateLabel,
} from '../../constants/scheduledEmail';
import { buildFullReminderPayload } from '../../utils/reminderUtils';
import { formatProposalAmount } from './proposalHistoryAmount';

export interface ReminderRecord {
  id: number;
  content?: string;
  reminderDate?: string;
  dueDate?: string;
  estimatedDurationMinutes?: number;
  resolvedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  state?: ReminderStateType;
  statusId?: number;
  status?: {
    id?: number;
    name?: string;
    color?: string;
  };
  activityType?: ActivityType;
  channel?: string;
  link?: string;
  account?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    id?: number | string;
    phoneNumber?: string;
    phone?: string;
    roles?: Array<{ title?: string; name?: string }>;
  };
  accountId?: number;
  entityName?: string;
  entityId?: string | number;
  company?: any;
  result?: string;
  properties?: Record<string, any> | null;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
}

type ScheduledEmailRecord = {
  id: number;
  email?: string;
  subject?: string;
  templateName?: string;
  type?: string;
  status?: string;
  scheduledAt?: string;
  createdAt?: string;
  attemptCount?: number;
  lastError?: string;
  createdBy?: number;
};

type ReferenceProposalSummary = {
  id?: number | string;
  proposalNumber?: string;
  proposalCode?: string;
  proposalNo?: string;
  createdAt?: string | null;
  proposalUrl?: string | null;
};

const STATE_COLOR_MAP: Record<ReminderStateType, string> = {
  PENDING: 'orange',
  PROCESSING: 'cyan',
  ACCEPT: 'green',
  REJECT: 'red',
  COMPLETED: 'blue',
  CLOSED: 'gray',
};


const DEFAULT_ACTIVITY_TYPE: ActivityType = ACTIVITY_TYPES[0];
const DEFAULT_ACTIVE_TAB = 'reminders';

const { TabPane } = Tabs;

interface EntityNotesDrawerProps {
  open: boolean;
  entityId: number | null | undefined;
  onClose: () => void;
  entityName?: string;
  title?: string;
  onAddReminder?: () => void;
  onEditReminder?: (reminder: ReminderRecord) => void;
  remindersVersion?: number;
  proposalStatusId?: number | string | null;
  proposalStatusName?: string | null;
  proposalStatusColor?: string | null;
  proposalCreatedAt?: string | null;
  proposalReferenceId?: number | string | null;
}

const EntityNotesDrawer: React.FC<EntityNotesDrawerProps> = ({
  open,
  entityId,
  onClose,
  entityName = 'proposal',
  title,
  onAddReminder,
  onEditReminder,
  remindersVersion = 0,
  proposalStatusId,
  proposalStatusName,
  proposalStatusColor,
  proposalCreatedAt,
  proposalReferenceId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reminders, setReminders] = useState<ReminderRecord[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [remindersPage, setRemindersPage] = useState(1);
  const [remindersPageSize, setRemindersPageSize] = useState(20);
  const [remindersTotal, setRemindersTotal] = useState(0);
  const [allReminders, setAllReminders] = useState<ReminderRecord[]>([]);
  const [allRemindersLoading, setAllRemindersLoading] = useState(false);
  const [allRemindersPage, setAllRemindersPage] = useState(1);
  const [allRemindersPageSize, setAllRemindersPageSize] = useState(20);
  const [allRemindersTotal, setAllRemindersTotal] = useState(0);
  const isAdmin = hasRole('ADMIN');
  const isCentralUser = hasRole('CENTRAL_USER');
  const canCreateReminder = isAdmin || isCentralUser;
  const canEditNotes = isAdmin || hasRole('CENTRAL_USER') || hasRole('FINANCE');
  const currentUserId = typeof window !== 'undefined' ? localStorage.getItem('id') : null;
  const [completionDrawerVisible, setCompletionDrawerVisible] = useState(false);
  const [completionTarget, setCompletionTarget] = useState<ReminderRecord | null>(null);
  const [completionResult, setCompletionResult] = useState('');
  const [completionSwitchActive, setCompletionSwitchActive] = useState(true);
  const [completionActivityType, setCompletionActivityType] = useState<ActivityType>(DEFAULT_ACTIVITY_TYPE);
  const [completionChannel, setCompletionChannel] = useState<string | undefined>(undefined);
  const [completionReminderDate, setCompletionReminderDate] = useState<dayjs.Dayjs | null>(null);
  const [completionContent, setCompletionContent] = useState('');
  const [completionSubmitting, setCompletionSubmitting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completionResultAttachments, setCompletionResultAttachments] = useState<UploadFile[]>([]);
  const [scheduledEmails, setScheduledEmails] = useState<ScheduledEmailRecord[]>([]);
  const [scheduledEmailsLoading, setScheduledEmailsLoading] = useState(false);
  const [scheduledEmailCancellingId, setScheduledEmailCancellingId] = useState<number | null>(null);
  const [proposalPdfHistory, setProposalPdfHistory] = useState<ProposalPdfArchiveHistoryItem[]>([]);
  const [proposalPdfHistoryLoading, setProposalPdfHistoryLoading] = useState(false);
  const [otherProposals, setOtherProposals] = useState<OtherProposalItem[]>([]);
  const [otherProposalsLoading, setOtherProposalsLoading] = useState(false);
  const [previousProposals, setPreviousProposals] = useState<OtherProposalItem[]>([]);
  const [previousProposalsLoading, setPreviousProposalsLoading] = useState(false);
  const [nextProposal, setNextProposal] = useState<ReferenceProposalSummary | null>(null);
  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);

  const normalizedEntityName = useMemo(
    () => (entityName ?? 'proposal').toString().toUpperCase(),
    [entityName]
  );

  useEffect(() => {
    if (!open) return;
    setActiveTab(DEFAULT_ACTIVE_TAB);
  }, [open, entityId, normalizedEntityName]);

  useEffect(() => {
    if (open && entityId && activeTab === 'reminders') {
      fetchReminders();
    }
  }, [
    open,
    entityId,
    activeTab,
    normalizedEntityName,
    remindersVersion,
    canEditNotes,
    remindersPage,
    remindersPageSize,
  ]);

  useEffect(() => {
    if (open && entityId && activeTab === 'all') {
      fetchAllReminders();
    }
  }, [
    open,
    entityId,
    activeTab,
    normalizedEntityName,
    remindersVersion,
    canEditNotes,
    allRemindersPage,
    allRemindersPageSize,
  ]);

  useEffect(() => {
    setRemindersPage(1);
    setAllRemindersPage(1);
  }, [entityId, normalizedEntityName]);


  const normalizeReminderResponse = (response: any) => {
    const payload = response?.data ?? response ?? {};
    const list: ReminderRecord[] = Array.isArray(payload?.content)
      ? payload.content
      : Array.isArray(payload)
        ? payload
        : [];
    const totalElements =
      typeof payload?.totalElements === 'number'
        ? payload.totalElements
        : typeof payload?.total === 'number'
          ? payload.total
          : null;
    return { list, totalElements };
  };

  const fetchReminders = async () => {
    if (!entityId) return;
    setRemindersLoading(true);
    try {
      const params: ReminderFilter = {
        entityNames: normalizedEntityName,
        entityId: entityId.toString(),
        page: remindersPage - 1,
        size: remindersPageSize,
      };
      if (isAdmin) {
        const response: any = await ReminderService.getAdminRemindersProxyAll(params);
        const { list, totalElements } = normalizeReminderResponse(response);
        setReminders(list);
        setRemindersTotal(totalElements ?? list.length);
      } else if (canEditNotes) {
        const response: any = await ReminderService.getAdminReminders(params);
        const { list, totalElements } = normalizeReminderResponse(response);
        setReminders(list);
        setRemindersTotal(totalElements ?? list.length);
      } else {
        const response: any = await ReminderService.getMyRemindersAll(params);
        const { list, totalElements } = normalizeReminderResponse(response);
        setReminders(list);
        setRemindersTotal(totalElements ?? list.length);
      }
    } catch (error) {
      openNotification('error', 'Hata', 'Hatırlatmalar yüklenirken bir hata oluştu.');
      console.error(error);
    } finally {
      setRemindersLoading(false);
    }
  };

  const fetchAllReminders = async () => {
    if (!entityId) return;
    setAllRemindersLoading(true);
    try {
      const params: ReminderFilter = {
        entityNames: normalizedEntityName,
        entityId: entityId.toString(),
        page: allRemindersPage - 1,
        size: allRemindersPageSize,
        sort: 'resolvedAt,DESC',
      };
      if (isAdmin) {
        const response: any = await ReminderService.getAdminRemindersProxyAllRaw(params);
        const { list, totalElements } = normalizeReminderResponse(response);
        setAllReminders(list);
        setAllRemindersTotal(totalElements ?? list.length);
      } else if (canEditNotes) {
        const response: any = await ReminderService.getAdminRemindersRaw(params);
        const { list, totalElements } = normalizeReminderResponse(response);
        setAllReminders(list);
        setAllRemindersTotal(totalElements ?? list.length);
      } else {
        const response: any = await ReminderService.getMyRemindersAllRaw(params);
        const { list, totalElements } = normalizeReminderResponse(response);
        setAllReminders(list);
        setAllRemindersTotal(totalElements ?? list.length);
      }
    } catch (error) {
      openNotification('error', 'Hata', 'İşlem geçmişi yüklenirken bir hata oluştu.');
      console.error(error);
    } finally {
      setAllRemindersLoading(false);
    }
  };

  const fetchScheduledEmails = useCallback(async () => {
    if (!entityId || normalizedEntityName !== 'PROPOSAL') {
      setScheduledEmails([]);
      return;
    }
    setScheduledEmailsLoading(true);
    try {
      const response = await http.get<ScheduledEmailRecord[]>(`/proposals/${entityId}/scheduled-emails`);
      setScheduledEmails(response.data ?? []);
    } catch (error) {
      console.error('Scheduled emails load failed', error);
      openNotification('error', 'Hata', 'E-posta geçmişi yüklenemedi.');
      setScheduledEmails([]);
    } finally {
      setScheduledEmailsLoading(false);
    }
  }, [entityId, normalizedEntityName]);

  const handleCancelScheduledEmail = useCallback(async (scheduledEmailId: number) => {
    if (!entityId || normalizedEntityName !== 'PROPOSAL') {
      return;
    }
    setScheduledEmailCancellingId(scheduledEmailId);
    try {
      await http.patch(`/proposals/${entityId}/scheduled-emails/${scheduledEmailId}/cancel`);
      openNotification('success', 'Başarılı', 'Planlı e-posta iptal edildi.');
      await fetchScheduledEmails();
    } catch (error: any) {
      const statusCode = error?.response?.status;
      if (statusCode === 409) {
        openNotification('warning', 'Uyarı', 'Bu planlı e-posta artık iptal edilemez.');
      } else if (statusCode === 404) {
        openNotification('warning', 'Uyarı', 'Planlı e-posta kaydı bulunamadı.');
      } else {
        openNotification('error', 'Hata', 'Planlı e-posta iptal edilemedi.');
      }
      console.error('Scheduled email cancel failed', error);
    } finally {
      setScheduledEmailCancellingId((current) => (current === scheduledEmailId ? null : current));
    }
  }, [entityId, normalizedEntityName, fetchScheduledEmails]);

  useEffect(() => {
    if (open && entityId && normalizedEntityName === 'PROPOSAL' && activeTab === 'emails') {
      fetchScheduledEmails();
    } else if (!open || !entityId || normalizedEntityName !== 'PROPOSAL') {
      setScheduledEmails([]);
    }
  }, [open, entityId, normalizedEntityName, activeTab, fetchScheduledEmails]);

  const fetchProposalPdfHistory = useCallback(async () => {
    if (!entityId || normalizedEntityName !== 'PROPOSAL') {
      setProposalPdfHistory([]);
      return;
    }

    setProposalPdfHistoryLoading(true);
    try {
      const response = await ProposalService.getPdfArchiveHistory(Number(entityId));
      setProposalPdfHistory(response.data ?? []);
    } catch (error) {
      console.error('Proposal PDF history load failed', error);
      openNotification('error', 'Hata', 'Teklif geçmişi yüklenemedi.');
      setProposalPdfHistory([]);
    } finally {
      setProposalPdfHistoryLoading(false);
    }
  }, [entityId, normalizedEntityName]);

  useEffect(() => {
    if (open && entityId && normalizedEntityName === 'PROPOSAL' && activeTab === 'proposal-history') {
      fetchProposalPdfHistory();
    } else if (!open || !entityId || normalizedEntityName !== 'PROPOSAL') {
      setProposalPdfHistory([]);
    }
  }, [open, entityId, normalizedEntityName, activeTab, fetchProposalPdfHistory]);

  const handleDownloadProposalPdf = (record: ProposalPdfArchiveHistoryItem) => {
    if (!record?.pdfUrl) {
      openNotification('warning', 'Uyarı', 'Teklif PDF bağlantısı bulunamadı.');
      return;
    }

    window.open(record.pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const fetchOtherProposals = useCallback(async () => {
    if (!entityId || normalizedEntityName !== 'PROPOSAL') {
      setOtherProposals([]);
      return;
    }

    setOtherProposalsLoading(true);
    try {
      const response = await ProposalService.getOtherProposals(Number(entityId));
      setOtherProposals(response.data ?? []);
    } catch (error) {
      console.error('Other proposals load failed', error);
      openNotification('error', 'Hata', 'Diğer teklifler yüklenemedi.');
      setOtherProposals([]);
    } finally {
      setOtherProposalsLoading(false);
    }
  }, [entityId, normalizedEntityName]);

  useEffect(() => {
    if (open && entityId && normalizedEntityName === 'PROPOSAL' && activeTab === 'other-proposals') {
      fetchOtherProposals();
    } else if (!open || !entityId || normalizedEntityName !== 'PROPOSAL') {
      setOtherProposals([]);
    }
  }, [open, entityId, normalizedEntityName, activeTab, fetchOtherProposals]);

  const fetchPreviousProposals = useCallback(async () => {
    if (!entityId || normalizedEntityName !== 'PROPOSAL') {
      setPreviousProposals([]);
      return;
    }

    setPreviousProposalsLoading(true);
    try {
      const response = await ProposalService.getPreviousProposals(Number(entityId));
      setPreviousProposals(response.data ?? []);
    } catch (error) {
      console.error('Previous proposals load failed', error);
      openNotification('error', 'Hata', 'Önceki teklifler yüklenemedi.');
      setPreviousProposals([]);
    } finally {
      setPreviousProposalsLoading(false);
    }
  }, [entityId, normalizedEntityName]);

  useEffect(() => {
    if (open && entityId && normalizedEntityName === 'PROPOSAL' && activeTab === 'previous-proposals') {
      fetchPreviousProposals();
    } else if (!open || !entityId || normalizedEntityName !== 'PROPOSAL') {
      setPreviousProposals([]);
    }
  }, [open, entityId, normalizedEntityName, activeTab, fetchPreviousProposals]);

  useEffect(() => {
    const referenceId = Number(proposalReferenceId);
    let isActive = true;

    if (
      !open ||
      normalizedEntityName !== 'PROPOSAL' ||
      proposalReferenceId === undefined ||
      proposalReferenceId === null ||
      proposalReferenceId === '' ||
      Number.isNaN(referenceId)
    ) {
      setNextProposal(null);
      return;
    }

    setNextProposal(null);
    ProposalService.get(referenceId)
      .then((response: any) => {
        if (!isActive) return;
        const proposal = response?.data ?? response;
        setNextProposal(proposal || null);
      })
      .catch((error) => {
        if (!isActive) return;
        console.error('Next proposal load failed', error);
        setNextProposal(null);
      });

    return () => {
      isActive = false;
    };
  }, [open, normalizedEntityName, proposalReferenceId]);

  const handleDownloadOtherProposal = (record: OtherProposalItem) => {
    if (!record?.proposalUrl) {
      openNotification('warning', 'Uyarı', 'Teklif PDF bağlantısı bulunamadı.');
      return;
    }

    window.open(record.proposalUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteReminder = async (reminderId: number) => {
    try {
      await ReminderService.remove(reminderId);
      openNotification('success', 'Başarılı', 'Görev silindi.');
      fetchReminders();
    } catch (error) {
      openNotification('error', 'Hata', 'Görev silinirken bir hata oluştu.');
      console.error(error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const renderProposalStatusTag = () => {
    if (!proposalStatusId && !proposalStatusName) return null;
    const baseColor = proposalStatusColor || '#d1d5db';
    const color = tinycolor(baseColor);
    const transparentColor = color.setAlpha(0.1).toRgbString();
    const transparentBorder = color.setAlpha(0.5).toRgbString();

    return (
      <Tag
        style={{
          color: baseColor,
          backgroundColor: transparentColor,
          borderColor: transparentBorder,
          borderRadius: '16px',
          padding: '4px 10px',
          fontSize: '13px',
          display: 'inline-block',
          minWidth: '100px',
          textAlign: 'center',
          fontWeight: 600,
        }}
      >
        {proposalStatusName || proposalStatusId}
      </Tag>
    );
  };

  const proposalStatusTagElement = renderProposalStatusTag();

  const proposalDateSummary = useMemo(() => {
    if (!proposalCreatedAt) return null;
    const createdAtDate = dayjs(proposalCreatedAt);
    if (!createdAtDate.isValid()) return null;

    return [
      { label: 'Oluşturulma Tarihi', value: createdAtDate.format('DD.MM.YYYY') },
      { label: 'Teklif Gönderildi', value: createdAtDate.add(30, 'day').format('DD.MM.YYYY') },
      { label: 'Manuel Takip', value: createdAtDate.add(60, 'day').format('DD.MM.YYYY') },
    ];
  }, [proposalCreatedAt]);

  const renderProposalSummaryLink = useCallback((proposal: ReferenceProposalSummary | OtherProposalItem) => {
    const proposalLabel =
      proposal.proposalNumber ||
      ('proposalCode' in proposal ? proposal.proposalCode : undefined) ||
      ('proposalNo' in proposal ? proposal.proposalNo : undefined) ||
      (proposal.id ? `Teklif #${proposal.id}` : null);

    if (!proposalLabel) return null;

    const createdAtDate = proposal.createdAt ? dayjs(proposal.createdAt) : null;
    const createdAtText = createdAtDate?.isValid() ? createdAtDate.format('DD.MM.YYYY') : null;
    const proposalNumberElement = proposal.proposalUrl ? (
      <a href={proposal.proposalUrl} target="_blank" rel="noopener noreferrer">
        {proposalLabel}
      </a>
    ) : (
      <span>{proposalLabel}</span>
    );

    return (
      <span>
        {proposalNumberElement}
        {createdAtText && <span> - {createdAtText}</span>}
      </span>
    );
  }, []);

  const previousProposalsSummary = useMemo(() => {
    const proposalLinks = previousProposals
      .map((proposal) => ({ proposal, element: renderProposalSummaryLink(proposal) }))
      .filter((item) => item.element);

    if (proposalLinks.length === 0) return null;

    return (
      <span style={{ color: '#374151', fontSize: 13 }}>
        <b>Önceki Teklifler:</b>{' '}
        {proposalLinks.map(({ proposal, element }, index) => (
          <React.Fragment key={proposal.id ?? index}>
            {index > 0 && <span>, </span>}
            {element}
          </React.Fragment>
        ))}
      </span>
    );
  }, [previousProposals, renderProposalSummaryLink]);

  const nextProposalSummary = useMemo(() => {
    if (!nextProposal) return null;
    const nextProposalElement = renderProposalSummaryLink(nextProposal);
    if (!nextProposalElement) return null;

    return (
      <span style={{ color: '#374151', fontSize: 13 }}>
        <b>Sonraki Teklif:</b> {nextProposalElement}
      </span>
    );
  }, [nextProposal, renderProposalSummaryLink]);

  const renderReminderStateTag = (state?: ReminderStateType) => {
    if (!state) return null;
    return <Tag color={STATE_COLOR_MAP[state] || 'default'}>{STATE_LABELS[state] || state}</Tag>;
  };

  const renderReminderAccountCell = (record: ReminderRecord) => {
    return <AccountCell {...getAssigneAccountCellProps(record)} />;
  };

  const renderReminderStatusBadge = (status?: ReminderRecord['status']) => {
    const statusName = status?.name;
    if (!statusName) return null;
    const baseColor = status?.color || '#d1d5db';
    const color = tinycolor(baseColor);
    const transparentColor = color.setAlpha(0.1).toRgbString();
    const transparentBorder = color.setAlpha(0.5).toRgbString();

    return (
      <Tag
        style={{
          color: baseColor,
          backgroundColor: transparentColor,
          borderColor: transparentBorder,
          borderRadius: '16px',
          padding: '4px 10px',
          fontSize: '13px',
          display: 'inline-block',
          minWidth: '100px',
          textAlign: 'center',
          fontWeight: 600,
        }}
      >
        {statusName}
      </Tag>
    );
  };

  const renderReminderChannelIcon = (channel?: string) => {
    if (!channel) return null;
    const meta = CHANNEL_META[channel];
    if (!meta) return null;
    return (
      <Tooltip title={meta.label}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            fontSize: 16,
            color: '#374151',
          }}
        >
          {meta.icon}
        </span>
      </Tooltip>
    );
  };

  const normalizeChannelValue = (channel?: string) => {
    if (!channel) return channel;
    if (channel === 'OFFLINE' || channel === 'LOCATION') return 'IN_PERSON';
    if (channel === 'MEET') return 'ONLINE';
    if (channel === 'DOCUMENT') return 'DOCUMENT_RECORD';
    if (channel === 'SYSTEM') return 'SOFTWARE_PORTAL_FORM';
    return channel;
  };

  const DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];
  const DOCUMENT_EXTS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx'];
  const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

  const extractExtension = (name: string) => {
    const clean = (name || '').split('?')[0].toLowerCase();
    const dotIndex = clean.lastIndexOf('.');
    return dotIndex >= 0 ? clean.slice(dotIndex) : '';
  };

  const resolveFileName = (file: Partial<UploadFile> | File) => {
    if ('name' in file && file.name) return file.name;
    if ('url' in file && typeof file.url === 'string') {
      const parts = file.url.split('/');
      return parts[parts.length - 1] || '';
    }
    return '';
  };

  const isImageFile = (file: Partial<UploadFile> | File) => {
    const type = 'type' in file ? file.type || '' : '';
    if (type.startsWith('image/')) return true;
    const ext = extractExtension(resolveFileName(file));
    return IMAGE_EXTS.includes(ext);
  };

  const isDocumentFile = (file: Partial<UploadFile> | File) => {
    const type = 'type' in file ? file.type || '' : '';
    if (DOCUMENT_MIME_TYPES.some((t) => type === t || type.startsWith(t))) return true;
    const ext = extractExtension(resolveFileName(file));
    return DOCUMENT_EXTS.includes(ext);
  };

  const splitAttachmentLists = (fileList: UploadFile[]) => {
    const images: UploadFile[] = [];
    const documents: UploadFile[] = [];
    fileList.forEach((file) => {
      if (isImageFile(file)) {
        images.push(file);
      } else if (isDocumentFile(file)) {
        documents.push(file);
      }
    });
    return { images, documents };
  };

  const buildUploadList = (urls?: string[], prefix: string = 'file') => {
    if (!Array.isArray(urls)) return [];
    return urls
      .filter((u) => typeof u === 'string' && u.length)
      .map((url, idx) => ({
        uid: `${prefix}-${idx}-${url}`,
        name: url.split('/').pop() || `file-${idx + 1}`,
        url,
        status: 'done' as const,
        type: prefix.includes('image') ? 'image/*' : 'application/octet-stream',
      }));
  };

  const parseResultAttachments = (raw?: any) => {
    if (!raw) return [];
    let parsed: any = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    const resultImages = Array.isArray(parsed?.resultImages) ? parsed.resultImages : [];
    const resultDocuments = Array.isArray(parsed?.resultDocuments) ? parsed.resultDocuments : [];
    return [
      ...buildUploadList(resultImages, 'result-image'),
      ...buildUploadList(resultDocuments, 'result-document'),
    ];
  };

  const getReminderOwnerId = (record: ReminderRecord) => record.accountId ?? record.account?.id;

  const isOwnedPendingReminder = (record: ReminderRecord) => {
    const ownerId = getReminderOwnerId(record);
    return (
      !isAdmin &&
      currentUserId &&
      ownerId !== undefined &&
      ownerId !== null &&
      String(ownerId) === String(currentUserId) &&
      record.state === 'PENDING'
    );
  };

  const openCompletionDrawer = (record: ReminderRecord) => {
    setCompletionTarget(record);
    setCompletionResult(record.result ?? '');
    setCompletionSwitchActive(true);
    setCompletionActivityType(record.activityType ?? DEFAULT_ACTIVITY_TYPE);
    setCompletionChannel(normalizeChannelValue(record.channel));
    setCompletionReminderDate(null);
    setCompletionContent(record.content ?? '');
    setCompletionResultAttachments(parseResultAttachments(record.properties));
    setCompletionError(null);
    setCompletionDrawerVisible(true);
  };

  const closeCompletionDrawer = () => {
    setCompletionDrawerVisible(false);
    setCompletionTarget(null);
    setCompletionResult('');
    setCompletionSwitchActive(true);
    setCompletionActivityType(DEFAULT_ACTIVITY_TYPE);
    setCompletionChannel(undefined);
    setCompletionReminderDate(null);
    setCompletionContent('');
    setCompletionError(null);
    setCompletionResultAttachments([]);
  };

  const resolveCompletionStatusId = async (target: ReminderRecord) => {
    const fallbackStatusId =
      target.statusId ??
      (target.status && typeof target.status === 'object' ? target.status.id : undefined);
    const isProposalEntity = (target.entityName || '').toString().toUpperCase() === 'PROPOSAL';
    const proposalId = Number(target.entityId);

    if (!isProposalEntity || Number.isNaN(proposalId)) {
      return fallbackStatusId;
    }

    try {
      const response: any = await ProposalService.get(proposalId);
      const proposal = response?.data ?? response;
      return proposal?.statusId ?? proposal?.status?.id ?? fallbackStatusId;
    } catch (error) {
      console.error('Proposal status could not be fetched for completion flow', error);
      return fallbackStatusId;
    }
  };

  const handleCompleteAction = (record: ReminderRecord) => {
    openCompletionDrawer(record);
  };

  const handleEditAction = (record: ReminderRecord) => {
    if (!onEditReminder) return;
    onEditReminder(record);
  };

  const handleCompletionSubmit = async () => {
    if (!completionTarget) return;
    if (!completionResult.trim()) {
      setCompletionError('Sonuç alanı boş bırakılamaz.');
      return;
    }
    if (completionSwitchActive) {
      if (!completionActivityType) {
        setCompletionError('Yeni görev için iş adımı seçmelisiniz.');
        return;
      }
      if (!completionChannel) {
        setCompletionError('Yeni görev için iletişim yöntemi seçmelisiniz.');
        return;
      }
      if (!completionReminderDate) {
        setCompletionError('Yeni görev için tarih seçmelisiniz.');
        return;
      }
      if (!completionContent.trim()) {
        setCompletionError('Yeni görev içeriği boş olamaz.');
        return;
      }
    }

    setCompletionSubmitting(true);
    try {
      const uploadFileList = async (fileList?: UploadFile[], code: string = 'reminder-result-file') => {
        if (!fileList || !fileList.length) return [];
        const results: string[] = [];
        for (const file of fileList) {
          if (file.url) {
            results.push(file.url);
            continue;
          }
          if (file.originFileObj) {
            const fd = new FormData();
            fd.append('code', code);
            fd.append('document', file.originFileObj);
            const resp = await http.post('/documents', fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const data = resp?.data ?? resp;
            const url = data?.documentUrl || data?.filePath || data?.url || data;
            if (url) results.push(url);
          }
        }
        return results;
      };

      const { images: resultImageFiles, documents: resultDocumentFiles } = splitAttachmentLists(completionResultAttachments);
      const [resultImageUrls, resultDocumentUrls] = await Promise.all([
        uploadFileList(resultImageFiles, 'reminder-result-image'),
        uploadFileList(resultDocumentFiles, 'reminder-result-file'),
      ]);

      let baseProps: any = {};
      if (completionTarget?.properties) {
        try {
          baseProps =
            typeof completionTarget.properties === 'string'
              ? JSON.parse(completionTarget.properties)
              : completionTarget.properties;
        } catch {
          baseProps = {};
        }
      }
      const mergedProps = {
        ...(baseProps || {}),
        ...(resultImageUrls.length
          ? { resultImages: resultImageUrls }
          : Array.isArray(baseProps?.resultImages)
            ? { resultImages: baseProps.resultImages }
            : {}),
        ...(resultDocumentUrls.length
          ? { resultDocuments: resultDocumentUrls }
          : Array.isArray(baseProps?.resultDocuments)
            ? { resultDocuments: baseProps.resultDocuments }
            : {}),
      };
      const properties =
        mergedProps && Object.keys(mergedProps).length ? JSON.stringify(mergedProps) : undefined;

      const completionPayload = buildFullReminderPayload(completionTarget, {
        state: 'COMPLETED',
        result: completionResult.trim(),
        ...(properties ? { properties } : {}),
      });

      await ReminderService.updateReminder(completionTarget.id, completionPayload);

      let successMessage = 'Görev tamamlandı.';

      if (completionSwitchActive && completionReminderDate) {
        const nextStatusId = await resolveCompletionStatusId(completionTarget);
        await ReminderService.createForMe({
          entityName: completionTarget.entityName,
          entityId: completionTarget.entityId,
          activityType: completionActivityType,
          channel: completionChannel,
          reminderDate: completionReminderDate.format('YYYY-MM-DDTHH:mm:ss'),
          content: completionContent.trim(),
          estimatedDurationMinutes: completionTarget.estimatedDurationMinutes,
          ...(nextStatusId ? { statusId: nextStatusId } : {}),
        });
        successMessage = 'Görev tamamlandı ve yeni görev oluşturuldu.';
      }

      openNotification('success', 'Başarılı', successMessage);
      closeCompletionDrawer();
      fetchReminders();
    } catch (error) {
      openNotification('error', 'Hata', 'Görev tamamlama sırasında bir hata oluştu.');
      console.error(error);
    } finally {
      setCompletionSubmitting(false);
    }
  };

  const getReminderResultValue = (record: ReminderRecord) => {
    if (record.result) return record.result;
    const raw = record.properties;
    if (!raw) return undefined;
    if (typeof raw === 'object') return (raw as any)?.result;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return parsed?.result;
      } catch {
        return undefined;
      }
    }
    return undefined;
  };

  const renderTaskContent = (record: ReminderRecord) => {
    const resultValue = getReminderResultValue(record);
    const statusTag = renderReminderStatusBadge(record.status);
    const channelIcon = renderReminderChannelIcon(record.channel);
    return (
      <div>
        {resultValue && (
          <div style={{ color: '#374151', fontSize: 13, marginBottom: 4 }}>
            <b>Sonuç:</b> {resultValue}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          {channelIcon}
          <Tooltip title={record.id ? `#${record.id}` : undefined}>
            <span>{record.content || 'Görev içeriği bulunamadı.'}</span>
          </Tooltip>
        </div>
        {statusTag && (
          <div style={{ marginTop: 6 }}>
            {statusTag}
          </div>
        )}
      </div>
    );
  };

  const handleAllRemindersPageChange = (page: number, pageSize?: number) => {
    setAllRemindersPage(page);
    if (pageSize && pageSize !== allRemindersPageSize) {
      setAllRemindersPageSize(pageSize);
    }
  };

  const handleRemindersPageChange = (page: number, pageSize?: number) => {
    setRemindersPage(page);
    if (pageSize && pageSize !== remindersPageSize) {
      setRemindersPageSize(pageSize);
    }
  };

  const reminderColumns = [
    {
      title: 'Kullanıcı',
      key: 'account',
      render: (_: any, record: ReminderRecord) => renderReminderAccountCell(record),
    },
    {
      title: 'Görev',
      dataIndex: 'content',
      key: 'content',
      render: (_: any, record: ReminderRecord) => renderTaskContent(record),
    },
    {
      title: 'Tarih',
      dataIndex: 'reminderDate',
      key: 'reminderDate',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Bitirme Tarihi',
      dataIndex: 'resolvedAt',
      key: 'resolvedAt',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Durum',
      dataIndex: 'state',
      key: 'state',
      render: (_: any, record: ReminderRecord) => {
        const stateTag = renderReminderStateTag(record.state);
        const statusTag = renderReminderStatusBadge(record.status);
        if (!stateTag && !statusTag) {
          return '-';
        }
        return (
          <Space direction="vertical" size={2}>
            {stateTag}
          </Space>
        );
      },
    },
    {
      title: 'İşlem',
      key: 'actions',
      render: (_: any, record: ReminderRecord) => {
        if (isAdmin) {
          const canComplete = record.state === 'PENDING';
          return (
            <div className="companies-action">
              {canComplete && (
                <Button
                  type="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCompleteAction(record);
                  }}
                >
                  Tamamla
                </Button>
              )}
              {onEditReminder && (
                <Button
                  icon={<img src={PenIcon} alt="pen" className="icon" />}
                  shape="circle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditAction(record);
                  }}
                  className="icon-button ml-12px"
                />
              )}
              <Popconfirm
                placement="left"
                title="Bu Görevi silmek istediğinize emin misiniz?"
                onConfirm={() => handleDeleteReminder(record.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                  shape="circle"
                  className="icon-button ml-12px"
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            </div>
          );
        }
        return isOwnedPendingReminder(record) ? (
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              handleCompleteAction(record);
            }}
          >
            Tamamla
          </Button>
        ) : (
          '-'
        );
      },
    },
  ];

  return (
    <Drawer
      title={title || 'Notlar ve Görevler'}
      open={open}
      onClose={handleClose}
      width={1200}
      destroyOnClose
    >
      {(proposalStatusTagElement || previousProposalsSummary || nextProposalSummary || proposalDateSummary) && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {proposalStatusTagElement}
          {previousProposalsSummary}
          {nextProposalSummary}
          {proposalDateSummary?.map((item) => (
            <span key={item.label} style={{ color: '#374151', fontSize: 13 }}>
              <b>{item.label}:</b> {item.value}
            </span>
          ))}
        </div>
      )}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Tümü" key="all">
          <AllRemindersTab
            loading={allRemindersLoading}
            dataSource={allReminders}
            page={allRemindersPage}
            pageSize={allRemindersPageSize}
            total={allRemindersTotal}
            onPageChange={handleAllRemindersPageChange}
            emptyText="Henüz işlem bulunmuyor."
          />
        </TabPane>
        <TabPane tab="Görevler" key="reminders">
          {canCreateReminder && onAddReminder && (
            <div className="d-flex justify-content-end mb-10px">
              <Button
                type="primary"
                className="d-button dark-button width-auto"
                onClick={onAddReminder}
              >
                Görev Ekle
              </Button>
            </div>
          )}
          <Table
            loading={remindersLoading}
            dataSource={reminders}
            columns={reminderColumns as any}
            pagination={{
              current: remindersPage,
              pageSize: remindersPageSize,
              total: remindersTotal,
              showSizeChanger: true,
              onChange: handleRemindersPageChange,
            }}
            rowKey="id"
            locale={{ emptyText: 'Herhangi bir işlem geçmişi bulunmuyor' }}
          />
        </TabPane>
        <TabPane tab="Statü Değişikliği" key="status-change">
          <StatusChangeReminders
            entityId={entityId}
            entityName={entityName}
            mode={canEditNotes ? 'admin' : 'user'}
            active={open && activeTab === 'status-change'}
          />
        </TabPane>
        <TabPane tab="Sorumlu Değişikliği" key="responsible-change">
          <ResponsibleChangeReminders
            entityId={entityId}
            entityName={entityName}
            mode={canEditNotes ? 'admin' : 'user'}
            active={open && activeTab === 'responsible-change'}
          />
        </TabPane>
        {normalizedEntityName === 'PROPOSAL' && (
          <TabPane tab="Teklif Geçmişi" key="proposal-history">
            <Table
              loading={proposalPdfHistoryLoading}
              dataSource={proposalPdfHistory}
              columns={[
                {
                  title: 'Teklif Numarası',
                  dataIndex: 'proposalNumber',
                  key: 'proposalNumber',
                  render: (value?: string) => value || '-',
                },
                {
                  title: 'Tarih/Saat',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
                },
                {
                  title: 'Teklif Tutarı',
                  dataIndex: 'totalPrice',
                  key: 'totalPrice',
                  render: formatProposalAmount,
                },
                {
                  title: 'İşlem',
                  key: 'actions',
                  width: 120,
                  render: (_: unknown, record: ProposalPdfArchiveHistoryItem) => (
                    <Button
                      size="small"
                      onClick={() => handleDownloadProposalPdf(record)}
                      disabled={!record?.pdfUrl}
                    >
                      İndir
                    </Button>
                  ),
                },
              ]}
              rowKey={(record) => String(record.id ?? `${record.proposalId}-${record.createdAt ?? record.pdfUrl}`)}
              pagination={false}
              scroll={{ x: 640 }}
              locale={{ emptyText: 'Henüz teklif geçmişi bulunmuyor.' }}
            />
          </TabPane>
        )}
        {normalizedEntityName === 'PROPOSAL' && (
          <TabPane tab="Önceki Teklifler" key="previous-proposals">
            <Table
              loading={previousProposalsLoading}
              dataSource={previousProposals}
              columns={[
                {
                  title: 'Teklif Numarası',
                  dataIndex: 'proposalNumber',
                  key: 'proposalNumber',
                  render: (value?: string) => value || '-',
                },
                {
                  title: 'Teklif Tarihi',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
                },
                {
                  title: 'Teklif Tutarı',
                  dataIndex: 'totalPrice',
                  key: 'totalPrice',
                  render: formatProposalAmount,
                },
                {
                  title: 'İndir',
                  key: 'download',
                  width: 120,
                  render: (_: unknown, record: OtherProposalItem) => (
                    <Button
                      size="small"
                      onClick={() => handleDownloadOtherProposal(record)}
                      disabled={!record?.proposalUrl}
                    >
                      İndir
                    </Button>
                  ),
                },
              ]}
              rowKey={(record) => String(record.id)}
              pagination={false}
              locale={{ emptyText: 'Bu teklife bağlı önceki teklif bulunmuyor.' }}
            />
          </TabPane>
        )}
        {normalizedEntityName === 'PROPOSAL' && (
          <TabPane tab="Diğer Teklifler" key="other-proposals">
            <Table
              loading={otherProposalsLoading}
              dataSource={otherProposals}
              columns={[
                {
                  title: 'Teklif Numarası',
                  dataIndex: 'proposalNumber',
                  key: 'proposalNumber',
                  render: (value?: string) => value || '-',
                },
                {
                  title: 'Teklif Tarihi',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
                },
                {
                  title: 'Teklif Tutarı',
                  dataIndex: 'totalPrice',
                  key: 'totalPrice',
                  render: formatProposalAmount,
                },
                {
                  title: 'İndir',
                  key: 'download',
                  width: 120,
                  render: (_: unknown, record: OtherProposalItem) => (
                    <Button
                      size="small"
                      onClick={() => handleDownloadOtherProposal(record)}
                      disabled={!record?.proposalUrl}
                    >
                      İndir
                    </Button>
                  ),
                },
              ]}
              rowKey={(record) => String(record.id)}
              pagination={false}
              locale={{ emptyText: 'Aynı firmaya ait başka teklif bulunmuyor.' }}
            />
          </TabPane>
        )}
        {normalizedEntityName === 'PROPOSAL' && (
          <TabPane tab="Planlı E-posta" key="emails">
              <Table
                loading={scheduledEmailsLoading}
                dataSource={scheduledEmails}
                columns={[
                {
                  title: 'E-posta',
                  dataIndex: 'email',
                  key: 'email',
                  render: (email: string, record: ScheduledEmailRecord) => (
                    <div>
                      <div>{email || '-'}</div>
                      {record.subject && (
                        <div style={{ color: '#6b7280', fontSize: 12 }}>{record.subject}</div>
                      )}
                    </div>
                  ),
                },
             
                {
                  title: 'E-posta',
                  dataIndex: 'templateName',
                  key: 'templateName',
                  render: (value?: string) => getScheduledEmailTemplateLabel(value),
                },
             
                {
                  title: 'Durum',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status?: string) => (
                    <Tag color={getScheduledEmailStatusColor(status)}>
                      {getScheduledEmailStatusLabel(status)}
                    </Tag>
                  ),
                },
                {
                  title: 'Deneme',
                  dataIndex: 'attemptCount',
                  key: 'attemptCount',
                  render: (attemptCount?: number) => attemptCount ?? 0,
                },
                {
                  title: 'Hata',
                  dataIndex: 'lastError',
                  key: 'lastError',
                  render: (lastError?: string) => lastError || '-',
                },
                {
                  title: 'Planlanan Tarih',
                  dataIndex: 'scheduledAt',
                  key: 'scheduledAt',
                  render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
                },
                {
                  title: 'Oluşturma Tarihi',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
                },
                {
                  title: 'İşlem',
                  key: 'actions',
                  render: (_: unknown, record: ScheduledEmailRecord) => {
                    if (record?.status !== 'PENDING') {
                      return '-';
                    }
                    return (
                      <Popconfirm
                        title='Bu planlı e-postayı iptal etmek istediğinize emin misiniz?'
                        okText='Evet'
                        cancelText='Hayır'
                        onConfirm={() => handleCancelScheduledEmail(record.id)}
                      >
                        <Button
                          danger
                          size='small'
                          loading={scheduledEmailCancellingId === record.id}
                          disabled={
                            scheduledEmailCancellingId !== null &&
                            scheduledEmailCancellingId !== record.id
                          }
                        >
                          İptal
                        </Button>
                      </Popconfirm>
                    );
                  },
                },
              ]}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: 'Henüz planlanan e-posta yok.' }}
            />
          </TabPane>
        )}
        <TabPane tab="Notlar" key="notes">
          <NoteReminders
            entityId={entityId}
            entityName={entityName}
            mode={canEditNotes ? 'admin' : 'user'}
            active={open && activeTab === 'notes'}
          />
        </TabPane>
      </Tabs>
      <CompletionDrawer
        open={completionDrawerVisible}
        onClose={closeCompletionDrawer}
        onSubmit={handleCompletionSubmit}
        submitting={completionSubmitting}
        error={completionError}
        result={completionResult}
        reminder={completionTarget}
        onResultChange={(value) => {
          setCompletionResult(value);
          if (completionError) setCompletionError(null);
        }}
        switchActive={completionSwitchActive}
        onSwitchChange={(checked) => {
          setCompletionSwitchActive(checked);
          if (!checked) {
            setCompletionError(null);
          }
        }}
        activityType={completionActivityType}
        onActivityTypeChange={(type) => {
          setCompletionActivityType(type);
          if (completionError) setCompletionError(null);
        }}
        channel={completionChannel}
        onChannelChange={(value) => {
          setCompletionChannel(value);
          if (completionError) setCompletionError(null);
        }}
        reminderDate={completionReminderDate}
        onReminderDateChange={(value) => {
          setCompletionReminderDate(value);
          if (completionError) setCompletionError(null);
        }}
        content={completionContent}
        onContentChange={(value) => {
          setCompletionContent(value);
          if (completionError) setCompletionError(null);
        }}
        resultAttachments={completionResultAttachments}
        onResultAttachmentsChange={(files) => setCompletionResultAttachments(files)}
      />
    </Drawer>
  );
};

export default EntityNotesDrawer;
